import React, {
    useEffect,
    useRef,
    useState,
} from "react";

import {
    ErrorMessage,
    Field,
    FormikProvider,
    useFormik,
    useFormikContext,
} from "formik";

import * as Yup from "yup";
import axios from "axios";
import ReactGA from "react-ga4";
import Select from "react-select";

import EmailIcon from "../assets/images/icons/icon-email.png";
import { US_STATES } from "./states.js";
import countryData from "./countries.json";
import countryCodes from "./countryCodes.json";
import { config } from "../utils/constants.js";

const REACT_APP_API_URL =
    config.url.REACT_APP_API_URL;

const MAX_CERTIFICATE_SIZE =
    10 * 1024 * 1024;

const API_TO_FORM_FIELD = {
    first_name: "contact.firstName",
    last_name: "contact.lastName",
    email: "contact.email",
    password: "contact.password",

    company_name: "business.companyName",
    business_phone: "business.phone_number",
    website: "business.website",

    business_address1: "business.street1",
    business_address2: "business.street2",
    business_city: "business.city",
    business_state: "business.state",
    business_zipcode: "business.zip",
    business_country: "business.country",

    shipping_same_as_business:
        "shippingSameAsBusiness",

    shipping_address1: "shipping.street1",
    shipping_address2: "shipping.street2",
    shipping_city: "shipping.city",
    shipping_state: "shipping.state",
    shipping_zipcode: "shipping.zip",
    shipping_country: "shipping.country",
    shipping_phone: "shipping.phone_number",

    resale_certificate_number:
        "resale.certificateNumber",

    resale_state: "resale.state",
    resale_certificate: "resale.certificate",
};

const getDialCode = (countryCode) => {
    if (
        !countryCode ||
        !countryCodes[countryCode]
    ) {
        return "";
    }

    return (
        countryCodes[countryCode].dial_code ||
        ""
    );
};

const stripToDigits = (value = "") =>
    String(value).replace(/\D/g, "");

const countDigits = (value = "") =>
    (
        String(value).match(/\d/g) || []
    ).length;

const formatPhoneForDisplay = (
    phone,
    country
) => {
    const raw = String(phone || "").trim();

    if (!raw) {
        return "";
    }

    const dialCode = getDialCode(country);
    const dialDigits =
        stripToDigits(dialCode);
    const digits = stripToDigits(raw);

    if (!digits) {
        return "";
    }

    if (!dialCode) {
        return raw.startsWith("+")
            ? `+${digits}`
            : digits;
    }

    if (raw.startsWith("+")) {
        return `+${digits}`;
    }

    if (
        dialDigits &&
        digits.startsWith(dialDigits)
    ) {
        const nationalNumber = digits.slice(
            dialDigits.length
        );

        return nationalNumber
            ? `${dialCode} ${nationalNumber}`
            : `${dialCode} `;
    }

    return `${dialCode} ${digits}`.trim();
};

const normalizePhoneForSubmit = (
    phone,
    country
) => {
    const raw = String(phone || "").trim();

    if (!raw) {
        return "";
    }

    const dialCode = getDialCode(country);
    const dialDigits =
        stripToDigits(dialCode);
    const digits = stripToDigits(raw);

    if (!digits) {
        return "";
    }

    if (raw.startsWith("+")) {
        return `+${digits}`;
    }

    if (
        dialDigits &&
        digits.startsWith(dialDigits)
    ) {
        return `+${digits}`;
    }

    if (dialDigits) {
        return `+${dialDigits}${digits}`;
    }

    return digits;
};

const normalizeStateForSubmit = (
    state,
    country
) => {
    const value = String(
        state || ""
    ).trim();

    if (country === "US") {
        return value.toUpperCase();
    }

    return value;
};

const getErrorText = (error) => {
    if (Array.isArray(error)) {
        return error
            .map(getErrorText)
            .filter(Boolean)
            .join(" ");
    }

    if (typeof error === "string") {
        return error;
    }

    if (
        error &&
        typeof error === "object"
    ) {
        return Object.values(error)
            .map(getErrorText)
            .filter(Boolean)
            .join(" ");
    }

    return "Invalid value.";
};

const CountrySelect = ({ fieldName }) => {
    const {
        values,
        setFieldValue,
        setFieldTouched,
    } = useFormikContext();

    const getCountryName = (
        label = ""
    ) =>
        label
            .replace(
                /^[^\p{L}\p{N}]+/gu,
                ""
            )
            .trim();

    const countries = React.useMemo(
        () => {
            const us =
                countryData.countries.find(
                    (country) =>
                        country.value === "US"
                );

            const remainingCountries =
                countryData.countries
                    .filter(
                        (country) =>
                            country.value !==
                            "US"
                    )
                    .slice()
                    .sort((a, b) =>
                        getCountryName(
                            a.label
                        ).localeCompare(
                            getCountryName(
                                b.label
                            ),
                            "en",
                            {
                                sensitivity:
                                    "base",
                            }
                        )
                    );

            return us
                ? [
                      us,
                      ...remainingCountries,
                  ]
                : remainingCountries;
        },
        []
    );

    const prefix =
        fieldName.split(".")[0];

    const addressValues =
        values[prefix] || {};

    const selectedCountry =
        addressValues.country || "";

    const currentPhone =
        addressValues.phone_number || "";

    const selectedOption =
        countries.find(
            (country) =>
                country.value ===
                selectedCountry
        ) || null;

    const handleCountryChange = (
        option
    ) => {
        const newCountry =
            option?.value || "";

        const newDialCode =
            getDialCode(newCountry);

        const previousDialCode =
            getDialCode(selectedCountry);

        const rawPhone = String(
            currentPhone || ""
        ).trim();

        setFieldValue(
            fieldName,
            newCountry,
            false
        );

        if (
            newCountry !== selectedCountry
        ) {
            setFieldValue(
                `${prefix}.state`,
                "",
                false
            );
        }

        if (!rawPhone) {
            setFieldValue(
                `${prefix}.phone_number`,
                newDialCode
                    ? `${newDialCode} `
                    : "",
                false
            );

            return;
        }

        const previousDialDigits =
            stripToDigits(
                previousDialCode
            );

        const newDialDigits =
            stripToDigits(newDialCode);

        const rawDigits =
            stripToDigits(rawPhone);

        let nationalDigits = rawDigits;

        if (
            previousDialDigits &&
            rawDigits.startsWith(
                previousDialDigits
            )
        ) {
            nationalDigits =
                rawDigits.slice(
                    previousDialDigits.length
                );
        }

        const nextPhone = newDialDigits
            ? `${newDialCode} ${nationalDigits}`.trim()
            : nationalDigits;

        setFieldValue(
            `${prefix}.phone_number`,
            nextPhone,
            false
        );
    };

    return (
        <Select
            name={fieldName}
            options={countries}
            value={selectedOption}
            onChange={
                handleCountryChange
            }
            onBlur={() =>
                setFieldTouched(
                    fieldName,
                    true
                )
            }
            classNamePrefix="react-select"
            className="react-select-container"
            placeholder="Select Country"
        />
    );
};

const StateField = ({ prefix }) => {
    const { values } =
        useFormikContext();

    const country =
        values[prefix]?.country;

    if (country === "US") {
        return (
            <Field
                as="select"
                name={`${prefix}.state`}
                className="form-select"
                autoComplete="address-level1"
            >
                <option value="">
                    Select State
                </option>

                {US_STATES.map(
                    ({
                        name,
                        abbreviation,
                    }) => (
                        <option
                            key={
                                abbreviation
                            }
                            value={
                                abbreviation
                            }
                        >
                            {name}
                        </option>
                    )
                )}
            </Field>
        );
    }

    return (
        <Field
            name={`${prefix}.state`}
            className="form-control"
            placeholder="State / Province / Region"
            autoComplete="address-level1"
        />
    );
};

const PhoneField = ({
    prefix,
    label = "Phone Number",
    requiredForUS = false,
}) => {
    const {
        values,
        setFieldValue,
    } = useFormikContext();

    const country =
        values[prefix]?.country;

    const isInternational =
        country && country !== "US";

    const isRequired =
        requiredForUS ||
        isInternational;

    const fieldName =
        `${prefix}.phone_number`;

    const fieldValue =
        values[prefix]?.phone_number ||
        "";

    const inputRef = useRef(null);

    const lastAutoFormattedRef =
        useRef("");

    const ensureDialCodeVisible = () => {
        const current = String(
            fieldValue || ""
        ).trim();

        if (!current) {
            const dialCode =
                getDialCode(country);

            if (dialCode) {
                setFieldValue(
                    fieldName,
                    `${dialCode} `,
                    false
                );
            }
        }
    };

    const syncPhoneValue = (
        rawValue
    ) => {
        const formatted =
            formatPhoneForDisplay(
                rawValue,
                country
            );

        if (
            formatted !== rawValue
        ) {
            setFieldValue(
                fieldName,
                formatted,
                false
            );
        }
    };

    useEffect(() => {
        const raw = String(
            fieldValue || ""
        ).trim();

        if (!raw) {
            lastAutoFormattedRef.current =
                "";

            return;
        }

        const formatted =
            formatPhoneForDisplay(
                raw,
                country
            );

        if (
            formatted &&
            formatted !== fieldValue &&
            formatted !==
                lastAutoFormattedRef.current
        ) {
            lastAutoFormattedRef.current =
                formatted;

            setFieldValue(
                fieldName,
                formatted,
                false
            );
        } else if (
            formatted === fieldValue
        ) {
            lastAutoFormattedRef.current =
                "";
        }
    }, [
        fieldValue,
        country,
        fieldName,
        setFieldValue,
    ]);

    return (
        <div className="m-b-20">
            <label
                htmlFor={fieldName}
                className="stext-102 cl3 p-b-8"
                style={{
                    display: "block",
                }}
            >
                {label}
                {isRequired ? " *" : ""}
            </label>

            <div className="bor8">
                <Field name={fieldName}>
                    {({ field }) => (
                        <input
                            {...field}
                            id={fieldName}
                            ref={inputRef}
                            type="tel"
                            className="stext-111 cl2 plh3 size-116 p-l-30 p-r-30"
                            placeholder={
                                isRequired
                                    ? "Enter phone number"
                                    : "Optional"
                            }
                            autoComplete="tel"
                            onFocus={
                                ensureDialCodeVisible
                            }
                            onChange={(
                                event
                            ) => {
                                const newValue =
                                    event
                                        .target
                                        .value;

                                setFieldValue(
                                    fieldName,
                                    newValue,
                                    false
                                );
                            }}
                            onBlur={(
                                event
                            ) => {
                                field.onBlur(
                                    event
                                );

                                syncPhoneValue(
                                    event
                                        .target
                                        .value
                                );
                            }}
                        />
                    )}
                </Field>
            </div>

            <ErrorMessage
                name={fieldName}
                component="div"
                className="text-danger mt-1 text-sm p-t-5"
            />
        </div>
    );
};

const TextInput = ({
    name,
    label,
    type = "text",
    placeholder = "",
    maxLength,
    autoComplete,
    icon = null,
}) => (
    <div className="m-b-20">
        <label
            htmlFor={name}
            className="stext-102 cl3 p-b-8"
            style={{
                display: "block",
            }}
        >
            {label}
        </label>

        <div
            className={`bor8 ${
                icon
                    ? "how-pos4-parent"
                    : ""
            }`}
        >
            <Field
                id={name}
                className={`stext-111 cl2 plh3 size-116 ${
                    icon
                        ? "p-l-62"
                        : "p-l-30"
                } p-r-30`}
                type={type}
                name={name}
                placeholder={placeholder}
                maxLength={maxLength}
                autoComplete={autoComplete}
            />

            {icon && (
                <img
                    className="how-pos4 pointer-none"
                    src={icon}
                    alt=""
                />
            )}
        </div>

        <ErrorMessage
            name={name}
            component="div"
            className="text-danger mt-1 text-sm p-t-5"
        />
    </div>
);

const makePhoneSchema = ({
    requiredForUS = false,
} = {}) =>
    Yup.string().when(
        "country",
        (country, schema) => {
            const resolvedCountry =
                Array.isArray(country)
                    ? country[0]
                    : country;

            const isInternational =
                Boolean(
                    resolvedCountry
                ) &&
                resolvedCountry !== "US";

            const isRequired =
                requiredForUS ||
                isInternational;

            return schema.test(
                "phone-valid-for-country",
                isRequired
                    ? "Enter a valid phone number with at least 6 digits"
                    : "Please enter a valid phone number",
                function (value) {
                    const raw = String(
                        value || ""
                    ).trim();

                    if (!raw) {
                        return !isRequired;
                    }

                    const normalized =
                        normalizePhoneForSubmit(
                            raw,
                            resolvedCountry
                        );

                    return (
                        countDigits(
                            normalized
                        ) >= 6
                    );
                }
            );
        }
    );

const makeAddressSchema = ({
    requirePhoneForUS = false,
} = {}) =>
    Yup.object({
        street1: Yup.string()
            .trim()
            .max(
                200,
                "Address is too long"
            )
            .required(
                "Street address is required"
            ),

        street2: Yup.string()
            .trim()
            .max(
                200,
                "Address is too long"
            ),

        city: Yup.string()
            .trim()
            .max(
                100,
                "City is too long"
            )
            .required("City is required"),

        country: Yup.string()
            .required(
                "Country is required"
            ),

        state: Yup.string()
            .trim()
            .max(
                100,
                "State or region is too long"
            )
            .required(
                "State, province, or region is required"
            ),

        zip: Yup.string()
            .trim()
            .max(
                20,
                "Postal code is too long"
            )
            .required(
                "ZIP or postal code is required"
            ),

        phone_number:
            makePhoneSchema({
                requiredForUS:
                    requirePhoneForUS,
            }),
    });

const emptyShippingSchema =
    Yup.object({
        street1: Yup.string(),
        street2: Yup.string(),
        city: Yup.string(),
        state: Yup.string(),
        zip: Yup.string(),
        country: Yup.string(),
        phone_number: Yup.string(),
    });

const WholesaleRegistrationForm = () => {
    const [
        serverMessage,
        setServerMessage,
    ] = useState("");

    const [
        serverError,
        setServerError,
    ] = useState("");

    const [
        isSubmitted,
        setIsSubmitted,
    ] = useState(false);

    const [
        verificationRequired,
        setVerificationRequired,
    ] = useState(false);

    const validationSchema =
        Yup.object({
            contact: Yup.object({
                firstName: Yup.string()
                    .trim()
                    .max(
                        128,
                        "First name is too long"
                    )
                    .required(
                        "First name is required"
                    ),

                lastName: Yup.string()
                    .trim()
                    .max(
                        128,
                        "Last name is too long"
                    )
                    .required(
                        "Last name is required"
                    ),

                email: Yup.string()
                    .trim()
                    .email(
                        "Enter a valid email address"
                    )
                    .required(
                        "Email is required"
                    ),

                password: Yup.string()
                    .min(
                        8,
                        "Password must contain at least 8 characters"
                    )
                    .max(
                        128,
                        "Password is too long"
                    )
                    .required(
                        "Password is required"
                    ),

                passwordConfirmation:
                    Yup.string()
                        .oneOf(
                            [
                                Yup.ref(
                                    "password"
                                ),
                            ],
                            "Passwords do not match"
                        )
                        .required(
                            "Confirm your password"
                        ),
            }),

            business:
                makeAddressSchema({
                    requirePhoneForUS:
                        true,
                }).shape({
                    companyName:
                        Yup.string()
                            .trim()
                            .max(
                                200,
                                "Company name is too long"
                            )
                            .required(
                                "Company name is required"
                            ),

                    website:
                        Yup.string()
                            .trim()
                            .url(
                                "Enter a valid website URL"
                            )
                            .notRequired(),
                }),

            shippingSameAsBusiness:
                Yup.boolean(),

            shipping: Yup.object().when(
                "shippingSameAsBusiness",
                {
                    is: false,

                    then: () =>
                        makeAddressSchema({
                            requirePhoneForUS:
                                false,
                        }),

                    otherwise: () =>
                        emptyShippingSchema,
                }
            ),

            resale: Yup.object({
                certificateNumber:
                    Yup.string()
                        .trim()
                        .max(
                            100,
                            "Certificate number is too long"
                        ),

                state: Yup.string()
                    .trim()
                    .test(
                        "state-length",
                        "Use the two-letter state abbreviation",
                        (value) =>
                            !value ||
                            value.length ===
                                2
                    ),

                certificate:
                    Yup.mixed()
                        .nullable()
                        .test(
                            "file-size",
                            "The resale certificate cannot exceed 10 MB",
                            (file) =>
                                !file ||
                                file.size <=
                                    MAX_CERTIFICATE_SIZE
                        )
                        .test(
                            "file-type",
                            "The resale certificate must be a PDF",
                            (file) =>
                                !file ||
                                file.type ===
                                    "application/pdf" ||
                                file.name
                                    ?.toLowerCase()
                                    .endsWith(
                                        ".pdf"
                                    )
                        ),
            }),
        });

    const formik = useFormik({
        initialValues: {
            contact: {
                firstName: "",
                lastName: "",
                email: "",
                password: "",
                passwordConfirmation:
                    "",
            },

            business: {
                companyName: "",
                phone_number: "",
                website: "",
                street1: "",
                street2: "",
                city: "",
                state: "",
                zip: "",
                country: "US",
            },

            shippingSameAsBusiness:
                true,

            shipping: {
                phone_number: "",
                street1: "",
                street2: "",
                city: "",
                state: "",
                zip: "",
                country: "US",
            },

            resale: {
                certificateNumber: "",
                state: "",
                certificate: null,
            },
        },

        validationSchema,

        onSubmit: async (
            values,
            {
                resetForm,
                setErrors,
                setFieldError,
                setSubmitting,
            }
        ) => {
            setServerMessage("");
            setServerError("");
            setErrors({});

            const registrationData =
                new FormData();

            registrationData.append(
                "first_name",
                values.contact.firstName.trim()
            );

            registrationData.append(
                "last_name",
                values.contact.lastName.trim()
            );

            registrationData.append(
                "email",
                values.contact.email
                    .trim()
                    .toLowerCase()
            );

            registrationData.append(
                "password",
                values.contact.password
            );

            registrationData.append(
                "company_name",
                values.business.companyName.trim()
            );

            registrationData.append(
                "business_phone",
                normalizePhoneForSubmit(
                    values.business.phone_number,
                    values.business.country
                )
            );

            registrationData.append(
                "website",
                values.business.website.trim()
            );

            registrationData.append(
                "business_address1",
                values.business.street1.trim()
            );

            registrationData.append(
                "business_address2",
                values.business.street2.trim()
            );

            registrationData.append(
                "business_city",
                values.business.city.trim()
            );

            registrationData.append(
                "business_state",
                normalizeStateForSubmit(
                    values.business.state,
                    values.business.country
                )
            );

            registrationData.append(
                "business_zipcode",
                values.business.zip.trim()
            );

            registrationData.append(
                "business_country",
                values.business.country
            );

            registrationData.append(
                "shipping_same_as_business",
                values.shippingSameAsBusiness
                    ? "true"
                    : "false"
            );

            if (
                !values.shippingSameAsBusiness
            ) {
                registrationData.append(
                    "shipping_address1",
                    values.shipping.street1.trim()
                );

                registrationData.append(
                    "shipping_address2",
                    values.shipping.street2.trim()
                );

                registrationData.append(
                    "shipping_city",
                    values.shipping.city.trim()
                );

                registrationData.append(
                    "shipping_state",
                    normalizeStateForSubmit(
                        values.shipping.state,
                        values.shipping.country
                    )
                );

                registrationData.append(
                    "shipping_zipcode",
                    values.shipping.zip.trim()
                );

                registrationData.append(
                    "shipping_country",
                    values.shipping.country
                );

                const shippingPhone =
                    normalizePhoneForSubmit(
                        values.shipping
                            .phone_number,
                        values.shipping.country
                    );

                if (shippingPhone) {
                    registrationData.append(
                        "shipping_phone",
                        shippingPhone
                    );
                }
            }

            registrationData.append(
                "resale_certificate_number",
                values.resale
                    .certificateNumber
                    .trim()
            );

            registrationData.append(
                "resale_state",
                values.resale.state
                    .trim()
                    .toUpperCase()
            );

            if (
                values.resale.certificate
            ) {
                registrationData.append(
                    "resale_certificate",
                    values.resale.certificate
                );
            }

            try {
                const response =
                    await axios.post(
                        `${REACT_APP_API_URL}/auth/register/`,
                        registrationData,
                        {
                            withCredentials:
                                true,
                        }
                    );

                const requiresVerification =
                    response.status === 202 ||
                    response.data
                        .verification_required ===
                        true;

                setVerificationRequired(
                    requiresVerification
                );

                setServerMessage(
                    response.data.message ||
                        (
                            requiresVerification
                                ? "Check your email to verify your registration."
                                : "Your wholesale registration form has been successfully " +
                                "submitted. You will receive a decision by email within " +
                                "48 hours."
                        )
                );

                setIsSubmitted(true);

                ReactGA.event({
                    category:
                        "engagement",
                    action:
                        "form_submission",
                    label:
                        requiresVerification
                            ? "Wholesale Registration Verification Required"
                            : "Wholesale Registration Submitted",
                });


                resetForm();
            } catch (error) {
                const responseData =
                    error.response?.data;

                if (
                    responseData &&
                    typeof responseData ===
                        "object"
                ) {
                    const generalErrors = [];

                    Object.entries(
                        responseData
                    ).forEach(
                        ([
                            apiField,
                            fieldError,
                        ]) => {
                            const formField =
                                API_TO_FORM_FIELD[
                                    apiField
                                ];

                            const errorText =
                                getErrorText(
                                    fieldError
                                );

                            if (formField) {
                                setFieldError(
                                    formField,
                                    errorText
                                );
                            } else {
                                generalErrors.push(
                                    errorText
                                );
                            }
                        }
                    );

                    if (
                        generalErrors.length >
                        0
                    ) {
                        setServerError(
                            generalErrors.join(
                                " "
                            )
                        );
                    } else {
                        setServerError(
                            "Please correct the highlighted fields."
                        );
                    }
                } else {
                    setServerError(
                        "An error occurred while submitting your registration. Please try again."
                    );
                }
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <FormikProvider value={formik}>
            <div className="w-full">
                {serverError &&
                !isSubmitted ? (
                    <div
                        className="alert alert-danger m-b-25"
                        role="alert"
                    >
                        {serverError}
                    </div>
                ) : null}

                {!isSubmitted ? (
                    <form
                        onSubmit={
                            formik.handleSubmit
                        }
                        encType="multipart/form-data"
                        noValidate
                    >
                        <h4 className="mtext-103 cl2 p-b-20">
                            Contact Information
                        </h4>

                        <div className="row">
                            <div className="col-md-6">
                                <TextInput
                                    name="contact.firstName"
                                    label="First Name"
                                    placeholder="First name"
                                    autoComplete="given-name"
                                />
                            </div>

                            <div className="col-md-6">
                                <TextInput
                                    name="contact.lastName"
                                    label="Last Name"
                                    placeholder="Last name"
                                    autoComplete="family-name"
                                />
                            </div>
                        </div>

                        <TextInput
                            name="contact.email"
                            label="Email Address"
                            type="email"
                            placeholder="Email address"
                            autoComplete="email"
                            icon={EmailIcon}
                        />

                        <div className="row">
                            <div className="col-md-6">
                                <TextInput
                                    name="contact.password"
                                    label="Password"
                                    type="password"
                                    placeholder="Create a password"
                                    autoComplete="new-password"
                                />
                            </div>

                            <div className="col-md-6">
                                <TextInput
                                    name="contact.passwordConfirmation"
                                    label="Confirm Password"
                                    type="password"
                                    placeholder="Confirm your password"
                                    autoComplete="new-password"
                                />
                            </div>
                        </div>

                        <hr className="m-t-20 m-b-30" />

                        <h4 className="mtext-103 cl2 p-b-20">
                            Business Information
                        </h4>

                        <TextInput
                            name="business.companyName"
                            label="Company Name"
                            placeholder="Company name"
                            autoComplete="organization"
                        />

                        <TextInput
                            name="business.website"
                            label="Website"
                            type="url"
                            placeholder="https://example.com"
                            autoComplete="url"
                        />

                        <TextInput
                            name="business.street1"
                            label="Business Street Address"
                            placeholder="Street address"
                            autoComplete="address-line1"
                        />

                        <TextInput
                            name="business.street2"
                            label="Business Address Line 2"
                            placeholder="Suite, unit, building, etc."
                            autoComplete="address-line2"
                        />

                        <div className="row">
                            <div className="col-md-6">
                                <TextInput
                                    name="business.city"
                                    label="Business City"
                                    placeholder="City"
                                    autoComplete="address-level2"
                                />
                            </div>

                            <div className="col-md-6">
                                <TextInput
                                    name="business.zip"
                                    label="Business ZIP / Postal Code"
                                    placeholder="75001"
                                    autoComplete="postal-code"
                                />
                            </div>

                            <div className="col-md-6 m-b-20">
                                <label className="stext-102 cl3 p-b-8 d-block">
                                    Business Country
                                </label>

                                <CountrySelect fieldName="business.country" />

                                <ErrorMessage
                                    name="business.country"
                                    component="div"
                                    className="text-danger mt-1 text-sm p-t-5"
                                />
                            </div>

                            <div className="col-md-6 m-b-20">
                                <label className="stext-102 cl3 p-b-8 d-block">
                                    Business State /
                                    Province / Region
                                </label>

                                <StateField prefix="business" />

                                <ErrorMessage
                                    name="business.state"
                                    component="div"
                                    className="text-danger mt-1 text-sm p-t-5"
                                />
                            </div>

                            <div className="col-md-6">
                                <PhoneField
                                    prefix="business"
                                    label="Business Phone"
                                    requiredForUS
                                />
                            </div>
                        </div>

                        <hr className="m-t-20 m-b-30" />

                        <h4 className="mtext-103 cl2 p-b-15">
                            Default Shipping Address
                        </h4>

                        <div className="form-check form-switch m-b-25">
                            <input
                                id="shippingSameAsBusiness"
                                className="form-check-input"
                                type="checkbox"
                                name="shippingSameAsBusiness"
                                checked={
                                    formik.values
                                        .shippingSameAsBusiness
                                }
                                onChange={
                                    formik.handleChange
                                }
                            />

                            <label
                                htmlFor="shippingSameAsBusiness"
                                className="form-check-label stext-102 cl3"
                            >
                                Use the business
                                address as the
                                default shipping
                                address
                            </label>
                        </div>

                        {!formik.values
                            .shippingSameAsBusiness && (
                            <>
                                <TextInput
                                    name="shipping.street1"
                                    label="Shipping Street Address"
                                    placeholder="Street address"
                                    autoComplete="shipping address-line1"
                                />

                                <TextInput
                                    name="shipping.street2"
                                    label="Shipping Address Line 2"
                                    placeholder="Suite, unit, building, etc."
                                    autoComplete="shipping address-line2"
                                />

                                <div className="row">
                                    <div className="col-md-6">
                                        <TextInput
                                            name="shipping.city"
                                            label="Shipping City"
                                            placeholder="City"
                                            autoComplete="shipping address-level2"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <TextInput
                                            name="shipping.zip"
                                            label="Shipping ZIP / Postal Code"
                                            placeholder="75001"
                                            autoComplete="shipping postal-code"
                                        />
                                    </div>

                                    <div className="col-md-6 m-b-20">
                                        <label className="stext-102 cl3 p-b-8 d-block">
                                            Shipping
                                            Country
                                        </label>

                                        <CountrySelect fieldName="shipping.country" />

                                        <ErrorMessage
                                            name="shipping.country"
                                            component="div"
                                            className="text-danger mt-1 text-sm p-t-5"
                                        />
                                    </div>

                                    <div className="col-md-6 m-b-20">
                                        <label className="stext-102 cl3 p-b-8 d-block">
                                            Shipping
                                            State /
                                            Province /
                                            Region
                                        </label>

                                        <StateField prefix="shipping" />

                                        <ErrorMessage
                                            name="shipping.state"
                                            component="div"
                                            className="text-danger mt-1 text-sm p-t-5"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <PhoneField
                                            prefix="shipping"
                                            label="Shipping Phone"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        <hr className="m-t-20 m-b-30" />

                        <h4 className="mtext-103 cl2 p-b-10">
                            Resale Certificate
                        </h4>

                        <p className="stext-102 cl6 p-b-20">
                            Certificate
                            information is
                            optional during
                            registration.
                            Tax-exempt status is
                            granted only after
                            the certificate has
                            been reviewed.
                        </p>

                        <div className="row">
                            <div className="col-md-8">
                                <TextInput
                                    name="resale.certificateNumber"
                                    label="Resale Certificate Number"
                                    placeholder="Certificate number"
                                />
                            </div>

                            <div className="col-md-4">
                                <TextInput
                                    name="resale.state"
                                    label="Issuing State"
                                    placeholder="TX"
                                    maxLength={2}
                                />
                            </div>
                        </div>

                        <div className="m-b-30">
                            <label
                                htmlFor="resaleCertificate"
                                className="stext-102 cl3 p-b-8"
                                style={{
                                    display:
                                        "block",
                                }}
                            >
                                Resale Certificate
                                PDF
                            </label>

                            <div className="bor8 p-l-20 p-r-20 p-t-15 p-b-15">
                                <input
                                    id="resaleCertificate"
                                    type="file"
                                    name="resale.certificate"
                                    accept=".pdf,application/pdf"
                                    onBlur={() =>
                                        formik.setFieldTouched(
                                            "resale.certificate",
                                            true
                                        )
                                    }
                                    onChange={(
                                        event
                                    ) => {
                                        const file =
                                            event
                                                .currentTarget
                                                .files?.[0] ||
                                            null;

                                        formik.setFieldValue(
                                            "resale.certificate",
                                            file
                                        );
                                    }}
                                />
                            </div>

                            <div className="stext-107 cl6 p-t-5">
                                PDF only. Maximum
                                file size: 10 MB.
                            </div>

                            <ErrorMessage
                                name="resale.certificate"
                                component="div"
                                className="text-danger mt-1 text-sm p-t-5"
                            />
                        </div>

                        <button
                            className="flex-c-m stext-101 cl0 size-121 bg3 bor1 hov-btn3 p-lr-15 trans-04 pointer"
                            type="submit"
                            disabled={
                                formik.isSubmitting
                            }
                        >
                            {formik.isSubmitting
                                ? "Submitting..."
                                : "Submit Wholesale Application"}
                        </button>
                    </form>
                ) : (
                    <div
                        className="txt-center p-t-20 p-b-30"
                        role="status"
                    >
                        <h4 className="mtext-105 cl2 p-b-20">
                            {verificationRequired
                                ? "Check Your Email"
                                : "Application Submitted"}
                        </h4>

                        <p className="stext-102 cl6">
                            {serverMessage}
                        </p>

                        {verificationRequired ? (
                            <p className="stext-107 cl6 p-t-15">
                                Your customer
                                information will
                                not be changed
                                until you use the
                                verification link
                                sent to your email
                                address.
                            </p>
                        ) : (
                            <p className="stext-107 cl6 p-t-15">
                                Wholesale account access and pricing will become
                                available only after your application has been
                                reviewed and approved.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </FormikProvider>
    );
};

export default WholesaleRegistrationForm;